import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { comics, categories } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find(c => c.slug === slug);
  const [showAllChapters, setShowAllChapters] = useState(false);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </div>
          <h2 className="text-lg font-semibold text-foreground-900">漫画不存在</h2>
          <Link to="/" className="inline-block mt-4 text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap">返回首页</Link>
        </div>
      </div>
    );
  }

  const cat = categories.find(c => c.slug === comic.category);
  const displayedChapters = showAllChapters ? comic.chapters : comic.chapters.slice(0, 10);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const schemaJson = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: `${comic.title} - 韩国漫画网`,
      description: comic.description,
      author: { '@type': 'Person', name: comic.author },
      datePublished: '2025-01-01',
      dateModified: comic.updatedAt,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://www.manhuawang.cn/comic/${comic.slug}`,
      },
      image: comic.coverImage,
      publisher: {
        '@type': 'Organization',
        name: '韩国漫画网',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.manhuawang.cn' },
        { '@type': 'ListItem', position: 2, name: `${cat?.name || comic.category}韩漫`, item: `https://www.manhuawang.cn/category/${comic.category}` },
        { '@type': 'ListItem', position: 3, name: comic.title, item: `https://www.manhuawang.cn/comic/${comic.slug}` },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title={`${comic.title} - ${comic.author} - 韩国漫画网`}
        description={comic.description}
        keywords={`${comic.title},${comic.author},韩国漫画,韩漫,免费漫画,在线阅读${comic.tags.map(t => `,${t}`).join('')}`}
        canonicalUrl={`https://www.manhuawang.cn/comic/${comic.slug}`}
        ogTitle={`${comic.title} - 韩国漫画网`}
        ogDescription={comic.description}
        ogImage={comic.coverImage}
        schemaJson={schemaJson}
      />
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-5xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: `${cat?.name || comic.category}韩漫`, href: `/category/${comic.category}` },
                { label: comic.title },
              ]}
            />

            <article className="flex flex-col md:flex-row gap-6 md:gap-8 mb-10">
              <div className="flex-shrink-0 w-full md:w-56">
                <div className="aspect-[3/4] rounded-lg overflow-hidden">
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-full h-full object-cover"
                    title={`${comic.title} - 韩国漫画网`}
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-bold text-foreground-900 font-heading">
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-500 mt-2">
                  作者：<strong className="text-foreground-700">{comic.author}</strong>
                </p>

                <div className="flex flex-wrap items-center gap-3 mt-4">
                  <span className={`px-3 py-1 text-xs rounded-full font-medium whitespace-nowrap ${
                    comic.status === '连载中'
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-foreground-100 text-foreground-700'
                  }`}>
                    {comic.status}
                  </span>
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-2.5 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                      {tag}
                    </span>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-4 mt-6 p-4 rounded-lg bg-background-100">
                  <div className="text-center">
                    <p className="text-lg font-bold text-primary-500">{formatViews(comic.views)}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">阅读量</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-accent-500">{comic.rating}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">评分</p>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-secondary-500">{comic.chapterCount}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">总话数</p>
                  </div>
                </div>

                <div className="mt-5">
                  <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {comic.longDescription}
                  </p>
                </div>
              </div>
            </article>

            <section className="pb-14">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-foreground-900 font-heading">章节目录</h2>
                <span className="text-xs text-foreground-400">共 {comic.chapterCount} 话</span>
              </div>

              <div className="space-y-0.5">
                {displayedChapters.map((chapter) => (
                  <div
                    key={chapter.id}
                    className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 flex items-center justify-center rounded-full bg-background-200 text-foreground-500 text-xs font-medium group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                        {chapter.number}
                      </div>
                      <span className="text-sm text-foreground-700 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                        {chapter.title}
                      </span>
                    </div>
                    <span className="text-xs text-foreground-400 whitespace-nowrap">{chapter.updatedAt}</span>
                  </div>
                ))}
              </div>

              {comic.chapters.length > 10 && (
                <div className="text-center mt-6">
                  <button
                    onClick={() => setShowAllChapters(!showAllChapters)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 text-sm text-primary-500 hover:text-primary-600 rounded-full bg-primary-50 hover:bg-primary-100 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {showAllChapters ? '收起' : `展开全部 ${comic.chapters.length} 话`}
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={showAllChapters ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
                    </div>
                  </button>
                </div>
              )}
            </section>

            <section className="pb-14">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-foreground-900 font-heading">相关推荐</h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                {comics
                  .filter(c => c.id !== comic.id && (c.category === comic.category || c.tags.some(t => comic.tags.includes(t))))
                  .slice(0, 6)
                  .map((related) => (
                    <Link
                      key={related.id}
                      to={`/comic/${related.slug}`}
                      className="group block"
                    >
                      <div className="aspect-[3/4] rounded-lg overflow-hidden mb-2">
                        <img
                          src={related.coverImage}
                          alt={related.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          title={`${related.title} - 韩国漫画网`}
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                        {related.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-0.5">{related.author}</p>
                    </Link>
                  ))}
              </div>
            </section>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}